<template>
  <SloModal
    :maxWidth="850"
    @formSubmit="save()"
    @dismiss="cancel()"
    class="edit-project"
  >
    <template v-slot:title>
      {{ 'Edit Project Details' | t }}
    </template>
    <section v-stack-y:lg>
      <h3>{{ 'Project Name' | t }}</h3>
      <SloField>
        <SloInput
          ref="input"
          type="text"
          :placeholder="'My Project' | t"
          name="name"
          :value="name"
          @input="updateProject('name', $event)"
        />
      </SloField>
      <SloField>
        <SloTextarea
          :placeholder="'Project notes' | t"
          name="notes"
          :value="notes"
          @input="updateProject('notes', $event)"
        />
      </SloField>
    </section>
    <section v-stack-y:lg>
      <div class="flex" v-stack-x:sm>
        <h3>{{ 'Location' | t }}</h3>
      </div>
      <form>
        <SloField>
          <SloLabel>{{ 'Firm Name' | t }}</SloLabel>
          <SloInput
            type="text"
            :placeholder="'e.g. “SOM Architects”' | t"
            name="company"
            :value="company"
            @input="updateProject('company', $event)"
          />
        </SloField>
        <SloField>
          <SloLabel>{{ 'Address' | t }}</SloLabel>
          <SloInput
            type="text"
            :placeholder="'e.g. “123 Michigan Ave, Suite 100”' | t"
            name="address"
            :value="address"
            @input="updateProject('address', $event)"
          />
        </SloField>
        <SloField>
          <SloLabel>{{ 'City' | t }}</SloLabel>
          <SloInput
            type="text"
            :placeholder="'e.g. “Chicago”' | t"
            name="city"
            :value="city"
            @input="updateProject('city', $event)"
          />
        </SloField>
        <SloField class="span-1">
          <SloLabel>{{ 'State' | t }}</SloLabel>
          <SloInput
            type="text"
            :placeholder="'e.g. “NY”' | t"
            name="state"
            :value="state"
            @input="updateProject('state', $event)"
          />
        </SloField>
        <SloField class="span-1">
          <SloLabel>{{ 'ZIP' | t }}</SloLabel>
          <SloInput
            type="text"
            :placeholder="'e.g. 60606' | t"
            name="zip"
            :value="zip"
            @input="updateProject('zip', $event)"
          />
        </SloField>
      </form>
    </section>
    <template v-slot:footer>
      <SloButton :disabled="loading" fill="outline" @click="cancel()">
        {{ 'Cancel' | t }}
      </SloButton>
      <SloButton :disabled="loading" type="submit">
        <SloLoader :loading="loading" size="md" color="white">
          {{ 'Save' | t }}
        </SloLoader>
      </SloButton>
    </template>
  </SloModal>
</template>

<script>
import t from '../../../main/i18n';
import {mapState, mapGetters, mapActions} from 'vuex';
import SloLoader from '../../../../lib/components/SloLoader.vue';
import SloModal from '../../../../lib/components/SloModal.vue';
import SloButton from '../../../../lib/components/SloButton.vue';
import SloField from '../../../../lib/components/SloField.vue';
import SloLabel from '../../../../lib/components/SloLabel.vue';
import SloInput from '../../../../lib/components/SloInput.vue';
import SloTextarea from '../../../../lib/components/SloTextarea.vue';

export default {
  components: {
    SloLoader,
    SloModal,
    SloButton,
    SloField,
    SloLabel,
    SloInput,
    SloTextarea
  },
  data() {
    return {
      loading: false,
      images: [],
      error: null,
      initialState: null
    };
  },
  filters: {
    t
  },
  created() {
    const project = this.getProject(Number(this.$route.params.id));

    if (!project) {
      this.$router.replace('/designspec');
      return;
    }

    const {name, notes, company, address, city, state, zip, cover_image_id} = project;
    this.initialState = {
      id: Number(this.$route.params.id),
      name,
      notes,
      company,
      address,
      city,
      state,
      zip,
      cover_image_id
    };
  },
  mounted() {
    if (this.project) {
      this.$nextTick(() => {
        this.$refs.input.setFocus();
      });
    }
  },
  computed: {
    ...mapGetters('designspec', {getProject: 'project'}),
    ...mapState('designspec', {coverImages: state => state.coverImages}),
    project() {
      return this.getProject(this.$route.params.id);
    },
    name() {
      return this.project?.name;
    },
    notes() {
      return this.project?.notes;
    },
    company() {
      return this.project?.company;
    },
    address() {
      return this.project?.address;
    },
    city() {
      return this.project?.city;
    },
    state() {
      return this.project?.state;
    },
    zip() {
      return this.project?.zip;
    },
    cover_image_id() {
      return this.project?.cover_image_id;
    }
  },
  methods: {
    ...mapActions('designspec', ['saveProject']),
    cancel() {
      this.$router.back();
      this.$store.dispatch('designspec/updateProject', this.initialState);
    },
    save() {
      this.loading = true;
      this.saveProject({id: this.project.id})
        .then(() => {
          this.loading = false;
          this.$snotify.success(t('Project updated'));
          this.$router.replace(`/designspec/project/${this.project.id}`);
        })
        .catch(() => {
          this.loading = false;
          this.$snotify.error(t('Project update failed'));
        });
    },
    updateProject(name, value) {
      this.$store.dispatch('designspec/updateProject', {
        id: this.project.id,
        [name]: value
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../../../scss/system';

h3 {
  @include text('base', 'strong');
}

form {
  display: flex;
  flex-flow: row wrap;

  .slo-field {
    flex: 1 0 50%;
  }

  .slo-field.span-1 {
    flex: 1 0 25%;
  }

  @supports (display: grid) {
    display: grid;
    gap: 32px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-template-rows: repeat(2, minmax(0, 1fr));

    .slo-field {
      grid-column: span 2;
    }

    .slo-field.span-1 {
      grid-column: span 1;
    }
  }
}

.flex {
  display: flex;
}

:deep(.slo-input) {
  textarea {
    margin: 0 !important;
  }
}
</style>
